import {useState, useEffect} from 'react';
import {Store} from '../models/store.model';
import {useStoreStore} from '../store/store';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {LoggerService} from '../../../Services/common/LoggerService';

interface UseEditStoreFormProps {
  store: Store | null;
  onSuccess?: (updatedStore: Store) => void;
  onClose: () => void;
}

export const useEditStoreForm = ({store, onSuccess, onClose}: UseEditStoreFormProps) => {
  const {updateStore} = useStoreStore();
  const errorHandlingService = StandardizedErrorHandler.getInstance();
  const {success: showSuccess, error: showError} = useNotification();
  const [formData, setFormData] = useState<Partial<Store>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{lat: number; lng: number} | null>(null);
  const [removedImages, setRemovedImages] = useState<{
    commercialRegisterImage?: string;
    taxCardImage?: string;
    profileImage?: string;
    storeImages?: string[];
  }>({});

  // Initialize form data when store changes
  useEffect(() => {
    if (store) {
      setFormData({
        name: store.name,
        address: store.address,
        managerName: store.managerName,
        developerId: store.developerId ?? null,
        developerName: store.developerName,
        phoneNumber: store.phoneNumber,
        region: store.region,
        city: store.city,
        latitude: store.latitude,
        longitude: store.longitude,
        notes: store.notes,
        commercialRegisterNumber: store.commercialRegisterNumber,
        taxCardNumber: store.taxCardNumber
      });
      setSelectedLocation({lat: store.latitude, lng: store.longitude});
    }
  }, [store]);

  const handleInputChange = (field: keyof Store, value: string) => {
    setFormData(prev => ({...prev, [field]: value}));
  };

  const handleDeveloperChange = (developerId: number | null) => {
    setFormData(prev => ({...prev, developerId}));
  };

  const handleFileChange = (field: keyof Store, file: File | File[] | null) => {
    setFormData(prev => ({...prev, [field]: file}));
  };

  const handleRemoveExistingImage = (imageType: 'commercialRegisterImage' | 'taxCardImage' | 'profileImage' | 'storeImages', imageUrl: string) => {
    setRemovedImages(prev => {
      if (imageType === 'storeImages') {
        const currentRemoved = prev.storeImages || [];
        return {
          ...prev,
          storeImages: [...currentRemoved, imageUrl]
        };
      } else {
        return {
          ...prev,
          [imageType]: imageUrl
        };
      }
    });
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setSelectedLocation({lat, lng});
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!store) {return;}

    setSubmitting(true);
    try {
      const finalLatitude = selectedLocation?.lat || store.latitude;
      const finalLongitude = selectedLocation?.lng || store.longitude;

      const updatedStore = await updateStore(store.id.toString(), {
        ...store,
        ...formData,
        latitude: finalLatitude,
        longitude: finalLongitude,
        removedImages: removedImages
      });

      // إذا كان الباك إند يعيد كائن فارغ، نستخدم البيانات المحلية
      const finalUpdatedStore = updatedStore.id ? updatedStore : {
        ...store,
        ...formData,
        latitude: finalLatitude,
        longitude: finalLongitude
      };

      showSuccess('تم تحديث بيانات المتجر بنجاح');
      onSuccess?.(finalUpdatedStore);
      onClose();
    } catch (error) {
      LoggerService.error('Error updating store:', error, 'useEditStoreForm');

      // Let StandardizedErrorHandler extract and display server error message
      // It will automatically show the appropriate notification with the server's error message
      errorHandlingService.handleError(error); // For notifications/logging
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({});
    setSelectedLocation(null);
    setShowMap(false);
    onClose();
  };

  const toggleMap = () => {
    setShowMap(prev => !prev);
  };

  return {
    formData,
    submitting,
    showMap,
    selectedLocation,
    removedImages,
    handleInputChange,
    handleDeveloperChange,
    handleFileChange,
    handleRemoveExistingImage,
    handleLocationSelect,
    handleSubmit,
    handleCancel,
    toggleMap
  };
};
